import {sortBy, sortedUniq} from 'lodash';
import {round} from 'lodash';

import {VizConfigItem} from 'src/components/Viz/config';

import {Site} from './api/project';
import {ApiForecastTimeSeries, ApiObservationTimeSeries} from './api/series';
import {TimeSeriesGraphDatum, TimeSeriesRangeGraphDatum} from './commonTypes';
import {formatDateShortFull} from './dates';

export type TableData = {
  [key: string]: number | string | null;
};

export function isValidDatum(datum: TimeSeriesGraphDatum | TimeSeriesRangeGraphDatum) {
  const [, ...values] = datum;
  return values.every((v) => v != null);
}

type DataByTimestep = {[key: string]: TableData};

export function getDataByTs(
  site: Site,
  observationTimeSeriesList: ApiObservationTimeSeries[] | undefined,
  forecastTimeSeriesList: ApiForecastTimeSeries[] | undefined,
  timeZone: string
): DataByTimestep {
  const tsISOStrings = sortedUniq(
    sortBy(
      [
        ...(observationTimeSeriesList ?? []).flatMap((ts) => ts.timestamps),
        ...(forecastTimeSeriesList ?? []).flatMap((ts) =>
          ts.forecasts.flatMap((f) => f.validTimes)
        ),
      ].map((d) => d.toISOString())
    )
  );

  const timeKey = `time (${timeZone.replaceAll('_', ' ')})`;

  // create an empty object for each t, as an isostring
  const dataByTs: DataByTimestep = tsISOStrings.reduce(
    (acc: object, t: string) => ({
      ...acc,
      [t]: {[timeKey]: formatDateShortFull(new Date(t), timeZone)},
    }),
    {}
  );
  (observationTimeSeriesList ?? []).forEach((ts) => {
    const modeSuffix = ts.query.rateVolumeMode ? `_${ts.query.rateVolumeMode}` : '';
    ts.timestamps.forEach((t, i) => {
      Object.keys(ts.data).forEach((k) => {
        dataByTs[t.toISOString()][`${ts.query.source}_${k}${modeSuffix}`] = ts.data[k][i];
      });
    });
  });
  (forecastTimeSeriesList ?? []).forEach((ts) => {
    const modeSuffix = ts.query.rateVolumeMode ? `_${ts.query.rateVolumeMode}` : '';
    ts.forecasts.forEach((f) => {
      f.validTimes.forEach((t, i) => {
        Object.keys(f.data).forEach((k) => {
          dataByTs[t.toISOString()][
            `${ts.query.source}_initialized_${formatDateShortFull(
              f.initializationTime,
              timeZone
            )}_${k}${modeSuffix}`
          ] = f.data[k][i];
        });
      });
    });
  });

  return dataByTs;
}

export type ObservationTimeSeriesById = {
  [key: string]: ApiObservationTimeSeries;
};
export type ForecastTimeSeriesById = {[key: string]: ApiForecastTimeSeries};

export function findMatchingTS<TS extends ApiObservationTimeSeries | ApiForecastTimeSeries>(
  configItem: VizConfigItem,
  tsList: TS[]
): TS | undefined {
  return (
    tsList &&
    tsList.find(
      (ts) =>
        ts.query.source === configItem.source &&
        configItem.columns.every((c) => ts.query.columns!.indexOf(c) > -1)
    )
  );
}

/**
 * The columns a forecast response actually returned data for — the union of the
 * keys across every forecast's `data` map.
 *
 * NOTE: we deliberately read `forecasts[].data` rather than `ts.variables`.
 * `variables` is built server-side from a *units* lookup, so columns without a
 * configured unit are dropped from it (and it can be empty entirely) even though
 * their data is present. `data` is the authoritative list of what will render.
 */
export function forecastReturnedColumns(ts: ApiForecastTimeSeries): Set<string> {
  return new Set(ts.forecasts.flatMap((f) => Object.keys(f.data)));
}

/**
 * Whether a forecast time series actually returned data for every column a
 * config needs. `findMatchingTS` matches on the *requested* columns
 * (`ts.query.columns`), but H5 forecasts return a narrower column set than
 * legacy HydroForecast (e.g. no q0.05/q0.25/q0.75/q0.95), so callers use this to
 * drop configs — like the 50%/90% CI bands — that have no data to render.
 *
 * Returns true when there is no matching series yet, or it has no forecasts yet
 * (loading/absent), to preserve existing behavior for that case.
 */
export function forecastTSHasColumns(
  configItem: VizConfigItem,
  ts: ApiForecastTimeSeries | undefined
): boolean {
  if (!ts?.forecasts.length) return true;
  const returned = forecastReturnedColumns(ts);
  return configItem.columns.every((c) => returned.has(c));
}

// Map each config item's id to its matching time series. Items with no match are
// omitted (not stored as undefined) so consumers can iterate the values safely.
export function buildTsById<TS extends ApiObservationTimeSeries | ApiForecastTimeSeries>(
  configItems: VizConfigItem[],
  tsList: TS[]
): {[id: string]: TS} {
  return configItems.reduce<{[id: string]: TS}>((acc, c) => {
    const ts = findMatchingTS(c, tsList);
    if (ts) acc[c.id] = ts;
    return acc;
  }, {});
}

export function isForecastTSEmpty(ts: ApiForecastTimeSeries | undefined): boolean {
  return !ts || ts.forecasts.length === 0;
}

export function isObservationTSEmpty(ts: ApiObservationTimeSeries | undefined): boolean {
  return !ts || Object.keys(ts.data).length === 0;
}
